import { useMutation, useQuery } from "@tanstack/react-query";
import { FormDto } from "../../../services/dto";
import { IActionResponse, IActionResult, SearchDto, SearchFilter } from "../../../assets/utils/lib";
import { QuestionnaireClient } from "../../../services/proxies/questionnaire";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { Constants } from "../../../assets/utils/lib/constants";
import { PushNotificationClient } from "../../../services/proxies";

export namespace QuestionnaireService {
    const questionnaireClient = new QuestionnaireClient();
    const pushNotificationClient = new PushNotificationClient();

  export const save = () =>
    useMutation<IActionResult<FormDto>, Error, FormDto>({
      mutationFn: async (payload: FormDto) => {
        return await questionnaireClient.saveQuestionnaireAsync(payload);
      },
    });

  export const getQuestionnaireQuery = (questionnaireId: string | undefined) =>
    useQuery({
      queryKey: ["questionnaireQuery", questionnaireId],
      queryFn: async () =>
        await questionnaireClient.getQuestionnaireAsync(questionnaireId),
      enabled: Boolean(questionnaireId) && questionnaireId != Constants.emptyGuid,
    });

    export const getQuestionnaireResponseQuery = (questionnaireId: string | undefined) =>
        useQuery({
            queryKey: ["questionnaireResponseChoiceQuery", questionnaireId],
            queryFn: async () =>
                await questionnaireClient.getQuestionnaireResponseAsync(questionnaireId),
            enabled: Boolean(questionnaireId) && questionnaireId != Constants.emptyGuid,
        });

    export const deActivateQuestionnaire = () =>
      useMutation<IActionResponse, Error, string>({
        mutationFn: async (communityId: string | undefined) => {
          return await questionnaireClient.deActivateQuestionnaireAsync(communityId);
        },
      });

    export const deleteQuestionnaire = () =>
        useMutation<IActionResponse, Error, string>({
            mutationFn: async (communityId: string | undefined) => {
                return await questionnaireClient.deleteQuestionnaireAsync(communityId);
            },
        });

    const hasCommunityId = (filters?: SearchFilter[]) =>
        !!filters?.some(
            (filter) => filter.field === "communityId" && filter.value && filter.value !== Constants.emptyGuid
        );

    export const gridQuery = (searchQuery?: SearchDto) => 
      useQuery({
        queryKey: ["questionnaireGridQuery", searchQuery],
        queryFn: async () =>
          await questionnaireClient.searchQuestionnairesAsync(searchQuery),
        enabled: hasCommunityId(searchQuery?.filters),
      });
    
    export const getLookups = () =>
        useQuery({
            queryKey: ["questionnaireLookupsQuery", "withNotificationType"],
            queryFn: async () => {
                const [questionnaire, push] = await Promise.all([
                    questionnaireClient.getLookupsAsync(),
                    pushNotificationClient.getLookupsAsync(),
                ]);
                return { questionnaire, push };
            },
            select: ({ questionnaire, push }) => ({
                community: LookupDto.mapToOptions(questionnaire?.community),
                userCommunities: LookupDto.mapToOptions(push?.userCommunities),
                questionnaireLookupValueType: LookupDto.mapToOptions(questionnaire?.questionnaireLookupValueType),
                tags: LookupDto.mapToOptions(questionnaire?.tags),
                questionTypes: LookupDto.mapToOptions(questionnaire?.surveyQuestionTypes),
                pollQuestionTypes: LookupDto.mapToOptions(questionnaire?.pollQuestionTypes),
                surveyQuestionTypes: LookupDto.mapToOptions(questionnaire?.surveyQuestionTypes),
                notificationType: LookupDto.mapToOptions(push?.notificationType),
                pageLink: LookupDto.mapToOptions(push?.pageLink),
            }),
        });
}
